<template>
  <div class="flex gap-6">
    <!-- Left: Study List (compact) -->
    <div class="w-64 shrink-0">
      <div class="flex items-center justify-between mb-3">
        <span class="text-sm font-medium text-gray-700">Studies</span>
        <button
          @click="newStudy"
          class="btn-primary-sm"
        >
          + New
        </button>
      </div>

      <div class="space-y-1 max-h-[calc(100vh-280px)] overflow-y-auto">
        <div
          v-for="study in studies"
          :key="study.id"
          @click="loadStudy(study)"
          :class="[
            'px-3 py-2 rounded cursor-pointer text-sm border',
            selectedStudyId === study.id
              ? 'bg-blue-50 border-blue-300'
              : 'bg-white border-gray-200 hover:border-gray-300'
          ]"
        >
          <div class="font-medium text-gray-900 truncate">{{ study.name }}</div>
          <div class="text-xs text-gray-500">{{ study.population_name }}</div>
        </div>
      </div>
    </div>

    <!-- Right: Pipeline Editor -->
    <div class="flex-1 space-y-4">
      <!-- Study Name -->
      <div class="flex items-center gap-4">
        <input
          v-model="config.name"
          type="text"
          placeholder="Study name..."
          class="flex-1 px-3 py-2 border border-gray-300 rounded text-sm font-medium"
        />
        <span v-if="config.is_builtin" class="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">Built-in</span>
      </div>

      <!-- Pipeline Sections -->
      <div class="space-y-2">

        <!-- 1. Population -->
        <div :class="['border rounded', validation.population?.error ? 'border-red-300' : 'border-gray-200']">
          <div
            @click="toggleSection('population')"
            :class="['flex items-center justify-between px-4 py-2 cursor-pointer', validation.population?.error ? 'bg-red-50' : 'bg-gray-50']"
          >
            <div class="flex items-center gap-2">
              <span class="text-xs font-mono text-gray-400">1</span>
              <span class="text-sm font-medium">Population</span>
              <span v-if="config.population_id" class="text-xs text-gray-500">
                {{ getPopulationName(config.population_id) }}
              </span>
              <span v-if="validation.population?.error" class="text-xs text-red-600 font-medium">Error</span>
              <span v-if="validation.population?.validating" class="text-xs text-gray-400">Validating...</span>
            </div>
            <svg :class="['w-4 h-4 transition-transform', expanded.population ? 'rotate-180' : '']" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <div v-show="expanded.population" class="px-4 py-3 border-t border-gray-200">
            <div>
              <label class="text-xs text-gray-600">Population base</label>
              <select v-model="config.population_id" @change="validatePopulation" class="w-full px-3 py-2 border border-gray-300 rounded text-sm">
                <option value="">Select population base...</option>
                <option v-for="pop in populations" :key="pop.id" :value="pop.id">
                  {{ pop.name }} ({{ pop.unit_type }})
                </option>
              </select>
              <p v-if="validation.population?.error" class="text-xs text-red-600 mt-1">{{ validation.population.error }}</p>
              <p v-else class="text-xs text-gray-500 mt-1">What does each row represent?</p>
            </div>
          </div>
        </div>

        <!-- 2. Inclusion Filters -->
        <div class="border border-gray-200 rounded">
          <div
            @click="toggleSection('filters')"
            class="flex items-center justify-between px-4 py-2 bg-gray-50 cursor-pointer"
          >
            <div class="flex items-center gap-2">
              <span class="text-xs font-mono text-gray-400">2</span>
              <span class="text-sm font-medium">Inclusion Filters</span>
              <span v-if="config.inclusion_criteria.length" class="text-xs text-gray-500">
                {{ config.inclusion_criteria.length }} filter(s)
              </span>
            </div>
            <svg :class="['w-4 h-4 transition-transform', expanded.filters ? 'rotate-180' : '']" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <div v-show="expanded.filters" class="px-4 py-3 border-t border-gray-200">
            <div v-for="(filter, idx) in config.inclusion_criteria" :key="idx" class="flex items-center gap-2 mb-2">
              <select v-model="filter.attribute_id" class="flex-1 px-2 py-1 border border-gray-300 rounded text-sm">
                <option value="">Attribute...</option>
                <option v-for="attr in attributes" :key="attr.id" :value="attr.id">{{ attr.name }}</option>
              </select>
              <select v-model="filter.operator" class="w-24 px-2 py-1 border border-gray-300 rounded text-sm">
                <option value="eq">=</option>
                <option value="neq">≠</option>
                <option value="gt">&gt;</option>
                <option value="gte">≥</option>
                <option value="lt">&lt;</option>
                <option value="lte">≤</option>
                <option value="is_null">is null</option>
                <option value="is_not_null">is not null</option>
              </select>
              <input
                v-if="!['is_null', 'is_not_null'].includes(filter.operator)"
                v-model="filter.value"
                class="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="value"
              />
              <button @click="removeFilter(idx)" class="text-red-500 hover:text-red-700">×</button>
            </div>
            <button @click="addFilter" class="text-xs text-blue-600 hover:text-blue-800">+ Add filter</button>
          </div>
        </div>

        <!-- 3. Treatment -->
        <div :class="['border rounded', validation.treatment?.error ? 'border-red-300' : 'border-gray-200']">
          <div
            @click="toggleSection('treatment')"
            :class="['flex items-center justify-between px-4 py-2 cursor-pointer', validation.treatment?.error ? 'bg-red-50' : 'bg-gray-50']"
          >
            <div class="flex items-center gap-2">
              <span class="text-xs font-mono text-gray-400">3</span>
              <span class="text-sm font-medium">Treatment</span>
              <span v-if="config.treatment_attribute_id" class="text-xs text-gray-500">
                {{ getAttributeName(config.treatment_attribute_id) }}
              </span>
              <span v-if="validation.treatment?.error" class="text-xs text-red-600 font-medium">Error</span>
              <span v-if="validation.treatment?.validating" class="text-xs text-gray-400">Validating...</span>
            </div>
            <svg :class="['w-4 h-4 transition-transform', expanded.treatment ? 'rotate-180' : '']" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <div v-show="expanded.treatment" class="px-4 py-3 border-t border-gray-200 space-y-2">
            <div>
              <label class="text-xs text-gray-600">Treatment attribute</label>
              <select v-model="config.treatment_attribute_id" @change="validateAttribute('treatment', config.treatment_attribute_id)" class="w-full px-3 py-2 border border-gray-300 rounded text-sm">
                <option value="">Select treatment...</option>
                <option v-for="attr in attributes" :key="attr.id" :value="attr.id">{{ attr.name }}</option>
              </select>
              <p v-if="validation.treatment?.error" class="text-xs text-red-600 mt-1">{{ validation.treatment.error }}</p>
            </div>
            <div>
              <label class="text-xs text-gray-600">Treatment value (what = treated?)</label>
              <input v-model="config.treatment_value" class="w-full px-3 py-2 border border-gray-300 rounded text-sm" placeholder="e.g., 1" />
            </div>
          </div>
        </div>

        <!-- 4. Outcomes -->
        <div :class="['border rounded', validation.primary_outcome?.error ? 'border-red-300' : 'border-gray-200']">
          <div
            @click="toggleSection('outcomes')"
            :class="['flex items-center justify-between px-4 py-2 cursor-pointer', validation.primary_outcome?.error ? 'bg-red-50' : 'bg-gray-50']"
          >
            <div class="flex items-center gap-2">
              <span class="text-xs font-mono text-gray-400">4</span>
              <span class="text-sm font-medium">Outcomes</span>
              <span v-if="config.primary_outcome_id" class="text-xs text-gray-500">
                {{ getAttributeName(config.primary_outcome_id) }}
              </span>
              <span v-if="validation.primary_outcome?.error" class="text-xs text-red-600 font-medium">Error</span>
              <span v-if="validation.primary_outcome?.validating" class="text-xs text-gray-400">Validating...</span>
            </div>
            <svg :class="['w-4 h-4 transition-transform', expanded.outcomes ? 'rotate-180' : '']" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <div v-show="expanded.outcomes" class="px-4 py-3 border-t border-gray-200 space-y-2">
            <div>
              <label class="text-xs text-gray-600">Primary outcome</label>
              <select v-model="config.primary_outcome_id" @change="validateAttribute('primary_outcome', config.primary_outcome_id)" class="w-full px-3 py-2 border border-gray-300 rounded text-sm">
                <option value="">Select outcome...</option>
                <option v-for="attr in attributes" :key="attr.id" :value="attr.id">{{ attr.name }}</option>
              </select>
              <p v-if="validation.primary_outcome?.error" class="text-xs text-red-600 mt-1">{{ validation.primary_outcome.error }}</p>
            </div>
            <div>
              <label class="text-xs text-gray-600">Secondary outcomes</label>
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="id in config.secondary_outcome_ids"
                  :key="id"
                  :class="['inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs', validation.secondaryOutcomes[id]?.error ? 'bg-red-100 text-red-700' : 'bg-gray-100']"
                  :title="validation.secondaryOutcomes[id]?.error || ''"
                >
                  {{ getAttributeName(id) }}
                  <button @click="removeSecondaryOutcome(id)" class="text-gray-400 hover:text-red-500">×</button>
                </span>
              </div>
              <select @change="addSecondaryOutcome($event)" class="mt-1 w-full px-2 py-1 border border-gray-300 rounded text-sm">
                <option value="">+ Add secondary outcome...</option>
                <option v-for="attr in attributes" :key="attr.id" :value="attr.id">{{ attr.name }}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- 5. Covariates -->
        <div :class="['border rounded', hasCovariatErrors ? 'border-red-300' : 'border-gray-200']">
          <div
            @click="toggleSection('covariates')"
            :class="['flex items-center justify-between px-4 py-2 cursor-pointer', hasCovariatErrors ? 'bg-red-50' : 'bg-gray-50']"
          >
            <div class="flex items-center gap-2">
              <span class="text-xs font-mono text-gray-400">5</span>
              <span class="text-sm font-medium">Covariates</span>
              <span v-if="config.covariate_ids.length" class="text-xs text-gray-500">
                {{ config.covariate_ids.length }} selected
              </span>
              <span v-if="hasCovariatErrors" class="text-xs text-red-600 font-medium">Errors</span>
            </div>
            <svg :class="['w-4 h-4 transition-transform', expanded.covariates ? 'rotate-180' : '']" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <div v-show="expanded.covariates" class="px-4 py-3 border-t border-gray-200">
            <div class="flex flex-wrap gap-1 mb-2">
              <span
                v-for="id in config.covariate_ids"
                :key="id"
                :class="['inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs', validation.covariates[id]?.error ? 'bg-red-100 text-red-700' : 'bg-gray-100']"
                :title="validation.covariates[id]?.error || ''"
              >
                {{ getAttributeName(id) }}
                <button @click="removeCovariate(id)" class="text-gray-400 hover:text-red-500">×</button>
              </span>
            </div>
            <select @change="addCovariate($event)" class="w-full px-2 py-1 border border-gray-300 rounded text-sm">
              <option value="">+ Add covariate...</option>
              <option v-for="attr in attributes" :key="attr.id" :value="attr.id">{{ attr.name }}</option>
            </select>
            <p class="text-xs text-gray-500 mt-1">Variables to control for in matching/regression</p>
          </div>
        </div>

        <!-- 6. Matching -->
        <div class="border border-gray-200 rounded">
          <div
            @click="toggleSection('matching')"
            class="flex items-center justify-between px-4 py-2 bg-gray-50 cursor-pointer"
          >
            <div class="flex items-center gap-2">
              <span class="text-xs font-mono text-gray-400">6</span>
              <span class="text-sm font-medium">Matching</span>
              <span v-if="config.matching_method" class="text-xs text-gray-500">
                {{ config.matching_method }}
              </span>
            </div>
            <svg :class="['w-4 h-4 transition-transform', expanded.matching ? 'rotate-180' : '']" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <div v-show="expanded.matching" class="px-4 py-3 border-t border-gray-200 space-y-2">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="text-xs text-gray-600">Method</label>
                <select v-model="config.matching_method" class="w-full px-2 py-1 border border-gray-300 rounded text-sm">
                  <option value="propensity_score">Propensity Score</option>
                  <option value="nearest_neighbor">Nearest Neighbor</option>
                </select>
              </div>
              <div>
                <label class="text-xs text-gray-600">Caliper</label>
                <input v-model="config.matching_config.caliper" type="number" step="0.1" class="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </div>
              <div>
                <label class="text-xs text-gray-600">Ratio</label>
                <input v-model="config.matching_config.ratio" type="number" class="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </div>
            </div>
          </div>
        </div>

        <!-- 7. Analysis -->
        <div class="border border-gray-200 rounded">
          <div
            @click="toggleSection('analysis')"
            class="flex items-center justify-between px-4 py-2 bg-gray-50 cursor-pointer"
          >
            <div class="flex items-center gap-2">
              <span class="text-xs font-mono text-gray-400">7</span>
              <span class="text-sm font-medium">Analysis</span>
              <span v-if="config.analysis_type" class="text-xs text-gray-500">
                {{ config.analysis_type }} / {{ config.outcome_model }}
              </span>
            </div>
            <svg :class="['w-4 h-4 transition-transform', expanded.analysis ? 'rotate-180' : '']" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <div v-show="expanded.analysis" class="px-4 py-3 border-t border-gray-200 space-y-2">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="text-xs text-gray-600">Type</label>
                <select v-model="config.analysis_type" class="w-full px-2 py-1 border border-gray-300 rounded text-sm">
                  <option value="matched_regression">Matched Regression</option>
                  <option value="did">Difference-in-Differences</option>
                  <option value="panel_fe">Panel Fixed Effects</option>
                </select>
              </div>
              <div>
                <label class="text-xs text-gray-600">Outcome Model</label>
                <select v-model="config.outcome_model" class="w-full px-2 py-1 border border-gray-300 rounded text-sm">
                  <option value="ols">OLS</option>
                  <option value="logit">Logit</option>
                  <option value="negbin">Negative Binomial</option>
                </select>
              </div>
            </div>
            <div v-if="config.analysis_type === 'did'" class="grid grid-cols-3 gap-2 pt-2 border-t border-gray-100">
              <div>
                <label class="text-xs text-gray-600">Pre months</label>
                <input v-model="config.analysis_config.pre_months" type="number" class="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </div>
              <div>
                <label class="text-xs text-gray-600">Post months</label>
                <input v-model="config.analysis_config.post_months" type="number" class="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </div>
              <div class="flex items-end">
                <label class="flex items-center gap-1 text-xs text-gray-600">
                  <input v-model="config.analysis_config.exclude_index" type="checkbox" class="rounded" />
                  Exclude index month
                </label>
              </div>
            </div>
          </div>
        </div>
        <!-- 8. Analysis Script -->
        <div class="border border-gray-200 rounded">
          <div
            @click="toggleSection('script')"
            class="flex items-center justify-between px-4 py-2 bg-gray-50 cursor-pointer"
          >
            <div class="flex items-center gap-2">
              <span class="text-xs font-mono text-gray-400">8</span>
              <span class="text-sm font-medium">Analysis Script</span>
              <span class="text-xs text-gray-500">R</span>
              <span v-if="config.template_key" class="text-xs px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                {{ config.template_key }}
              </span>
            </div>
            <svg :class="['w-4 h-4 transition-transform', expanded.script ? 'rotate-180' : '']" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <div v-show="expanded.script" class="px-4 py-3 border-t border-gray-200 space-y-2">
            <div v-if="!config.script" class="flex items-center gap-2">
              <select v-model="selectedTemplate" class="flex-1 px-2 py-1 border border-gray-300 rounded text-sm">
                <option value="">Start from a template...</option>
                <option v-for="t in templates" :key="t.key" :value="t.key">{{ t.key }}</option>
              </select>
              <button @click="applyTemplate" :disabled="!selectedTemplate" class="btn-primary-sm">Use template</button>
            </div>
            <textarea
              v-model="config.script"
              rows="18"
              spellcheck="false"
              class="w-full px-3 py-2 border border-gray-300 rounded text-xs font-mono leading-relaxed"
              placeholder="# The study's R script. Reads the frame via ks_frame(), params via ks_params(), emits via ks_effect() / ks_table() / ks_plot()."
            ></textarea>
            <p class="text-xs text-gray-500">
              Runs against the materialized frame (frame.csv + params.json). Emit results with
              <code>ks_effect()</code>, <code>ks_table()</code>, <code>ks_plot()</code>, <code>ks_diagnostic()</code>.
            </p>
          </div>
        </div>
      </div>

      <!-- Save / Run -->
      <div class="flex justify-end items-center gap-2">
        <span v-if="saveError" class="text-xs text-red-600">{{ saveError }}</span>
        <span v-if="hasAnyErrors" class="text-xs text-red-600">Fix validation errors before running</span>
        <button @click="saveStudy" :disabled="saving || !config.name" class="btn-primary">
          {{ saving ? 'Saving...' : 'Save' }}
        </button>
        <button
          @click="runStudy"
          :disabled="!canRun || running || hasAnyErrors"
          class="btn-primary"
        >
          {{ running ? runPhase : 'Run Study' }}
        </button>
      </div>

      <!-- Results -->
      <div v-if="results" class="border border-gray-200 rounded p-4 space-y-4">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium">Results</span>
          <span class="text-xs text-gray-500">{{ formatDate(results.run_at) }} · {{ results.status }}</span>
        </div>

        <div v-if="results.status === 'failed'" class="text-sm text-red-600 whitespace-pre-wrap">{{ results.error_message }}</div>
        <div v-else-if="results.status === 'pending' || results.status === 'running'" class="text-sm text-gray-500">
          Running — building frame and executing script...
        </div>

        <!-- Effects -->
        <div v-if="scriptEffects.length" class="grid grid-cols-2 gap-3">
          <div v-for="e in scriptEffects" :key="e.name" class="border border-gray-200 rounded p-3">
            <div class="text-xs text-gray-500">{{ e.name }}</div>
            <div class="text-xl font-semibold" :class="e.p < 0.05 ? 'text-green-700' : 'text-gray-900'">
              {{ formatEffectValue(e) }}
            </div>
            <div class="text-xs text-gray-500">
              95% CI {{ formatEffectNum(e.ci_low, e.unit) }} to {{ formatEffectNum(e.ci_high, e.unit) }}
              · p={{ e.p < 0.001 ? '<0.001' : e.p?.toFixed(3) }}
            </div>
            <div class="text-xs text-gray-400 mt-1">{{ e.model }}</div>
          </div>
        </div>

        <!-- Legacy summary (non-script runs) -->
        <div v-if="!scriptEffects.length && results.results?.outcomes?.primary" class="grid grid-cols-4 gap-4 text-sm">
          <div>
            <div class="text-xs text-gray-500">Population</div>
            <div class="font-medium">{{ results.results?.population_size?.toLocaleString() || '-' }}</div>
          </div>
          <div>
            <div class="text-xs text-gray-500">Effect</div>
            <div class="font-medium">
              {{ formatEffect(results.results.outcomes.primary.effect) }}
              <span class="text-xs text-gray-400">(p={{ results.results.outcomes.primary.pvalue?.toFixed(3) }})</span>
            </div>
          </div>
        </div>

        <!-- Diagnostics -->
        <div v-if="scriptDiagnostics.length" class="text-xs text-gray-600 space-y-1">
          <div v-for="d in scriptDiagnostics" :key="d.name">
            <span class="font-medium">{{ d.name }}:</span> {{ d.detail }}
          </div>
        </div>

        <!-- Plots -->
        <div v-if="results.artifacts?.length" class="grid grid-cols-2 gap-4">
          <div v-for="a in results.artifacts" :key="a.name" class="border border-gray-100 rounded p-2">
            <div class="text-xs font-medium text-gray-700 mb-1">{{ a.display_name || a.name }}</div>
            <img :src="artifactUrl(a)" :alt="a.display_name || a.name" class="w-full" />
            <div v-if="a.caption" class="text-xs text-gray-400 mt-1">{{ a.caption }}</div>
          </div>
        </div>

        <!-- Tables -->
        <div v-for="t in scriptTables" :key="t.name" class="overflow-x-auto">
          <div class="text-xs font-medium text-gray-700 mb-1">{{ t.name }}</div>
          <table class="min-w-full text-xs border border-gray-200">
            <thead>
              <tr class="bg-gray-50">
                <th v-for="c in t.columns" :key="c" class="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-200">{{ c }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in t.rows" :key="i" class="border-b border-gray-100">
                <td v-for="c in t.columns" :key="c" class="px-2 py-1">{{ formatCell(row[c]) }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="t.caption" class="text-xs text-gray-400 mt-1">{{ t.caption }}</div>
        </div>

        <!-- Console -->
        <details v-if="results.console_output">
          <summary class="text-xs text-gray-500 cursor-pointer">Console output</summary>
          <pre class="sensitive text-xs bg-gray-900 text-gray-100 rounded p-3 mt-1 overflow-x-auto max-h-64" data-demo-blur>{{ results.console_output }}</pre>
        </details>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'StudyBuilder',
  props: {
    attributes: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      studies: [],
      populations: [],
      templates: [],
      selectedTemplate: '',
      selectedStudyId: null,
      loading: true,
      running: false,
      runPhase: 'Running...',
      saving: false,
      saveError: null,
      results: null,
      pollTimer: null,
      expanded: {
        population: true,
        filters: false,
        treatment: true,
        outcomes: true,
        covariates: false,
        matching: false,
        analysis: false,
        script: false,
      },
      config: this.emptyConfig(),
      validation: {
        population: null,
        treatment: null,
        primary_outcome: null,
        secondaryOutcomes: {},
        covariates: {},
      },
    };
  },
  computed: {
    canRun() {
      return this.config.population_id &&
             this.config.treatment_attribute_id &&
             this.config.primary_outcome_id;
    },
    hasCovariatErrors() {
      return Object.values(this.validation.covariates).some(v => v?.error);
    },
    hasAnyErrors() {
      if (this.validation.population?.error) return true;
      if (this.validation.treatment?.error) return true;
      if (this.validation.primary_outcome?.error) return true;
      if (Object.values(this.validation.secondaryOutcomes).some(v => v?.error)) return true;
      if (Object.values(this.validation.covariates).some(v => v?.error)) return true;
      return false;
    },
    scriptEffects() {
      return this.results?.results?.effects || [];
    },
    scriptTables() {
      return this.results?.results?.tables || [];
    },
    scriptDiagnostics() {
      return this.results?.results?.diagnostics || [];
    },
  },
  methods: {
    emptyConfig() {
      return {
        name: '',
        is_builtin: false,
        population_id: null,
        inclusion_criteria: [],
        treatment_attribute_id: null,
        treatment_value: '1',
        primary_outcome_id: null,
        secondary_outcome_ids: [],
        covariate_ids: [],
        matching_method: 'propensity_score',
        matching_config: { caliper: 0.2, ratio: 1 },
        analysis_type: 'matched_regression',
        outcome_model: 'ols',
        analysis_config: { pre_months: 12, post_months: 12, exclude_index: true },
        script: '',
        template_key: '',
        params: {},
      };
    },
    savePayload() {
      return {
        name: this.config.name,
        population_id: this.config.population_id,
        inclusion_criteria: this.config.inclusion_criteria.filter(f => f.attribute_id),
        treatment_attribute_id: this.config.treatment_attribute_id,
        treatment_value: { value: this.config.treatment_value },
        primary_outcome_id: this.config.primary_outcome_id,
        secondary_outcome_ids: this.config.secondary_outcome_ids,
        covariate_ids: this.config.covariate_ids,
        matching_method: this.config.matching_method,
        matching_config: this.config.matching_config,
        analysis_type: this.config.analysis_type,
        outcome_model: this.config.outcome_model,
        analysis_config: this.config.analysis_config,
        script: this.config.script,
        script_language: 'r',
        template_key: this.config.template_key,
        params: this.config.params,
      };
    },
    async saveStudy() {
      this.saving = true;
      this.saveError = null;
      try {
        let response;
        if (this.selectedStudyId) {
          response = await axios.put(`api/impact_analytics/studies/${this.selectedStudyId}/`, this.savePayload());
        } else {
          response = await axios.post('api/impact_analytics/studies/', this.savePayload());
          this.selectedStudyId = response.data.id;
        }
        await this.fetchStudies();
        return true;
      } catch (error) {
        const detail = error.response?.data;
        this.saveError = typeof detail === 'object'
          ? Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join('; ').slice(0, 200)
          : 'Save failed';
        return false;
      } finally {
        this.saving = false;
      }
    },
    async fetchTemplates() {
      try {
        const response = await axios.get('api/impact_analytics/study-templates/');
        this.templates = response.data;
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    },
    applyTemplate() {
      const t = this.templates.find(x => x.key === this.selectedTemplate);
      if (!t) return;
      this.config.script = t.script;
      this.config.template_key = t.key;
    },
    artifactUrl(a) {
      return `/api/impact_analytics/study-runs/${this.results.id}/artifact/?name=${encodeURIComponent(a.name)}`;
    },
    formatEffectNum(v, unit) {
      if (v === null || v === undefined) return '-';
      const n = Number(v);
      const formatted = Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: Math.abs(n) < 10 ? 2 : 0 });
      const sign = n < 0 ? '-' : '';
      return unit === 'dollars' ? `${sign}$${formatted}` : `${sign}${formatted}`;
    },
    formatEffectValue(e) {
      return this.formatEffectNum(e.estimate, e.unit);
    },
    formatCell(v) {
      if (v === null || v === undefined) return '';
      if (typeof v === 'number' && !Number.isInteger(v)) return v.toFixed(Math.abs(v) < 1 ? 3 : 1);
      return v;
    },
    pollRun(runId) {
      this.pollTimer = setInterval(async () => {
        try {
          const response = await axios.get(`api/impact_analytics/study-runs/${runId}/`);
          this.results = response.data;
          if (['completed', 'failed'].includes(response.data.status)) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
            this.running = false;
          }
        } catch (error) {
          clearInterval(this.pollTimer);
          this.pollTimer = null;
          this.running = false;
        }
      }, 2500);
    },
    resetValidation() {
      this.validation = {
        population: null,
        treatment: null,
        primary_outcome: null,
        secondaryOutcomes: {},
        covariates: {},
      };
    },
    async validatePopulation() {
      if (!this.config.population_id) {
        this.validation.population = null;
        return;
      }
      this.validation.population = { validating: true };
      try {
        const response = await axios.get(`api/impact_analytics/populations/${this.config.population_id}/validate/`);
        this.validation.population = response.data.valid ? { valid: true } : { error: response.data.error };
      } catch (error) {
        this.validation.population = { error: error.response?.data?.error || 'Validation failed' };
      }
    },
    async validateAttribute(key, id) {
      if (!id) {
        this.validation[key] = null;
        return;
      }
      this.validation[key] = { validating: true };
      try {
        const response = await axios.get(`api/impact_analytics/attributes/${id}/validate/`);
        this.validation[key] = response.data.valid ? { valid: true } : { error: response.data.error };
      } catch (error) {
        this.validation[key] = { error: error.response?.data?.error || 'Validation failed' };
      }
    },
    async validateAttributeInCollection(collection, id) {
      if (!id) return;
      this.validation[collection][id] = { validating: true };
      try {
        const response = await axios.get(`api/impact_analytics/attributes/${id}/validate/`);
        this.validation[collection][id] = response.data.valid ? { valid: true } : { error: response.data.error };
      } catch (error) {
        this.validation[collection][id] = { error: error.response?.data?.error || 'Validation failed' };
      }
    },
    async validateAllComponents() {
      // Validate population
      if (this.config.population_id) {
        await this.validatePopulation();
      }
      // Validate treatment
      if (this.config.treatment_attribute_id) {
        await this.validateAttribute('treatment', this.config.treatment_attribute_id);
      }
      // Validate primary outcome
      if (this.config.primary_outcome_id) {
        await this.validateAttribute('primary_outcome', this.config.primary_outcome_id);
      }
      // Validate secondary outcomes
      for (const id of this.config.secondary_outcome_ids) {
        await this.validateAttributeInCollection('secondaryOutcomes', id);
      }
      // Validate covariates
      for (const id of this.config.covariate_ids) {
        await this.validateAttributeInCollection('covariates', id);
      }
    },
    async fetchStudies() {
      try {
        const response = await axios.get('api/impact_analytics/studies/');
        this.studies = response.data;
      } catch (error) {
        console.error('Error fetching studies:', error);
      }
    },
    async fetchPopulations() {
      try {
        const response = await axios.get('api/impact_analytics/populations/');
        this.populations = response.data;
      } catch (error) {
        console.error('Error fetching populations:', error);
      }
    },
    toggleSection(section) {
      this.expanded[section] = !this.expanded[section];
    },
    newStudy() {
      this.selectedStudyId = null;
      this.config = this.emptyConfig();
      this.resetValidation();
      this.results = null;
      this.expanded.population = true;
      this.expanded.treatment = true;
      this.expanded.outcomes = true;
    },
    async loadStudy(study) {
      this.selectedStudyId = study.id;
      this.results = null;
      this.resetValidation();

      // Fetch full study details
      try {
        const response = await axios.get(`api/impact_analytics/studies/${study.id}/`);
        const s = response.data;

        this.config = {
          name: s.name,
          is_builtin: s.is_builtin,
          population_id: s.population?.id || s.population_id,
          inclusion_criteria: s.inclusion_criteria || [],
          treatment_attribute_id: s.treatment_attribute?.id || s.treatment_attribute_id,
          treatment_value: s.treatment_value?.value ?? s.treatment_value ?? '1',
          primary_outcome_id: s.primary_outcome?.id || s.primary_outcome_id,
          secondary_outcome_ids: (s.secondary_outcomes || []).map(o => o.id || o),
          covariate_ids: (s.covariates || []).map(c => c.id || c),
          matching_method: s.matching_method || 'propensity_score',
          matching_config: s.matching_config || { caliper: 0.2, ratio: 1 },
          analysis_type: s.analysis_type || 'matched_regression',
          outcome_model: s.outcome_model || 'ols',
          analysis_config: s.analysis_config || { pre_months: 12, post_months: 12, exclude_index: true },
          script: s.script || '',
          template_key: s.template_key || '',
          params: s.params || {},
        };

        // Expand sections that have selections
        Object.keys(this.expanded).forEach(k => this.expanded[k] = false);
        if (this.config.population_id) this.expanded.population = true;
        if (this.config.treatment_attribute_id) this.expanded.treatment = true;
        if (this.config.primary_outcome_id) this.expanded.outcomes = true;
        if (this.config.covariate_ids.length) this.expanded.covariates = true;
        if (this.config.matching_method) this.expanded.matching = true;
        if (this.config.analysis_type) this.expanded.analysis = true;
        if (this.config.script) this.expanded.script = true;

        // Validate all components
        await this.validateAllComponents();
      } catch (error) {
        console.error('Error loading study details:', error);
      }
    },
    getPopulationName(id) {
      const pop = this.populations.find(p => p.id === id);
      return pop ? pop.name : '';
    },
    getAttributeName(id) {
      const attr = this.attributes.find(a => a.id === id);
      return attr ? attr.name : '';
    },
    addFilter() {
      this.config.inclusion_criteria.push({ attribute_id: null, operator: 'eq', value: '' });
    },
    removeFilter(idx) {
      this.config.inclusion_criteria.splice(idx, 1);
    },
    async addSecondaryOutcome(event) {
      const id = parseInt(event.target.value);
      if (id && !this.config.secondary_outcome_ids.includes(id)) {
        this.config.secondary_outcome_ids.push(id);
        await this.validateAttributeInCollection('secondaryOutcomes', id);
      }
      event.target.value = '';
    },
    removeSecondaryOutcome(id) {
      this.config.secondary_outcome_ids = this.config.secondary_outcome_ids.filter(i => i !== id);
      delete this.validation.secondaryOutcomes[id];
    },
    async addCovariate(event) {
      const id = parseInt(event.target.value);
      if (id && !this.config.covariate_ids.includes(id)) {
        this.config.covariate_ids.push(id);
        await this.validateAttributeInCollection('covariates', id);
      }
      event.target.value = '';
    },
    removeCovariate(id) {
      this.config.covariate_ids = this.config.covariate_ids.filter(i => i !== id);
      delete this.validation.covariates[id];
    },
    async runStudy() {
      if (!this.canRun || this.hasAnyErrors) return;
      this.running = true;
      this.runPhase = 'Saving...';
      this.results = null;

      const saved = await this.saveStudy();
      if (!saved) {
        this.running = false;
        return;
      }

      this.runPhase = 'Running...';
      try {
        const response = await axios.post(`api/impact_analytics/studies/${this.selectedStudyId}/run/`, {});
        this.results = response.data;
        if (['pending', 'running'].includes(response.data.status)) {
          this.pollRun(response.data.id);
        } else {
          this.running = false;
        }
      } catch (error) {
        console.error('Error running study:', error);
        this.saveError = error.response?.data?.error || error.message;
        this.running = false;
      }
    },
    formatDate(isoString) {
      if (!isoString) return '';
      return new Date(isoString).toLocaleString();
    },
    formatEffect(value) {
      if (value === null || value === undefined) return '-';
      const formatted = Math.abs(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
      return value < 0 ? `-$${formatted}` : `$${formatted}`;
    },
  },
  mounted() {
    this.fetchStudies();
    this.fetchPopulations();
    this.fetchTemplates();
  },
  beforeUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
};
</script>
